/**
 * Machines (hubs): which one is online, what it runs, which phones hang off it — and, per phone, **what
 * it is called** and **which TikTok account lives on it**.
 *
 * One phone = one TikTok account is the hard rule of this product, not a preference: a second phone
 * signed into the same account is how accounts get linked and banned together. The label is ours (the
 * phone cannot tell us), and the service refuses a label that is already on another phone. This screen
 * is where the label is set and where a refusal is shown in the service's own words.
 *
 * Deletion shows *every* phone a machine has, offline ones included — those are exactly the ones worth
 * cleaning out, and they are invisible on the workspace screen once they stop reporting.
 */

import { BindDirection, DirectionChip, useDirections } from '../marketing/directions'
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'
import { useState } from 'react'

import { ApiError } from '../../api/client'
import type { Device, Hub } from '../../api/types'
import { phoneName } from '../devices/phoneName'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { toast } from '../../lib/toast'
import { Badge, Card, Empty, Pill, Status } from '../../ui/components'
import { ConfirmDelete } from '../../ui/ConfirmDelete'
import { BatteryChip, DropCount, UsbSeat, UsbState, usbPlace } from './UsbWhere'
import { PhoneIcon } from '../../ui/PhoneIcon'

type Target = { kind: 'hub'; hub: Hub; phones: number } | { kind: 'device'; device: Device }

export function HubsPanel() {
  const { api, auth, authEpoch } = useApp()
  const t = useT()
  //: Removing hardware is admin-only, enforced by the service. Disabled with the reason, never hidden:
  //: a control that vanishes leaves somebody hunting for a feature they used before.
  const mayManage = auth.isAdmin
  const dirs = useDirections()
  const [binding, setBinding] = useState<Device | null>(null)
  const hubs = usePoll(() => api.hubs(), 10000, [api, authEpoch])
  const devices = usePoll(() => api.devices(), 10000, [api, authEpoch])
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [target, setTarget] = useState<Target | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const byHub = new Map<string, Device[]>()
  for (const d of devices.data ?? []) {
    if (!d.hub_id) continue
    const list = byHub.get(d.hub_id) ?? []
    list.push(d)
    byHub.set(d.hub_id, list)
  }

  const refresh = () => {
    hubs.refresh()
    devices.refresh()
  }

  const doDelete = async () => {
    if (!target) return
    setBusy(true)
    setError(null)
    try {
      if (target.kind === 'hub') {
        await api.deleteHub(target.hub.hub_id)
        toast(t.machines.deleted(target.hub.hub_id))
      } else {
        await api.deleteDevice(target.device.device_id)
        toast(t.machines.deleted(phoneName(target.device)))
      }
      setTarget(null)
      refresh()
    } catch (e) {
      const detail = e instanceof ApiError ? (e.detail as { detail?: { hint?: string } })?.detail : null
      setError(detail?.hint ?? (e instanceof Error ? e.message : String(e)))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Card
        title={t.machines.title}
        sub={t.machines.rule}
        actions={
          <button className="ghost icon" onClick={refresh} title={t.devices.refresh} aria-label={t.devices.refresh}>
            <RefreshCw className="ico" size={15} aria-hidden />
          </button>
        }
      >
        {hubs.error && <Status text={t.machines.readError(hubs.error.message)} bad />}
        {!hubs.error && (hubs.data ?? []).length === 0 && <Empty>{t.machines.none}</Empty>}

        {(hubs.data ?? []).map((h) => {
          const phones = byHub.get(h.hub_id) ?? []
          const onlinePhones = phones.filter((p) => p.status !== 'offline').length
          const unlabelled = phones.filter((p) => !p.tiktok_account).length
          const expanded = open[h.hub_id] ?? true
          return (
            <div key={h.hub_id} style={{ borderBottom: '1px solid var(--line-soft)', padding: 'var(--sp-3) 0' }}>
              <div className="row">
                {/* **The state first** (redesign stage 4, as on Device Lab's machines): whether the machine
                    is on the wire is the question this row answers before anything else. */}
                <Badge kind={h.online ? 'ok' : 'bad'}>{h.online ? t.machines.onWire : t.machines.offline}</Badge>
                <span style={{ minWidth: 0 }}>
                  <b>{h.hub_id}</b>
                  <div className="muted small">
                    {t.machines.point(h.point)}
                    {h.agent_version ? ` · ${t.machines.agent(h.agent_version)}` : ''}
                  </div>
                  {h.platform?.os && (
                    <div className="muted small">
                      {h.platform.os}
                      {h.platform.python ? ` · python ${h.platform.python}` : ''}
                    </div>
                  )}
                </span>
                <Pill>
                  {t.machines.phones(phones.length)}
                  {onlinePhones !== phones.length ? ` · ${t.machines.onlinePhones(onlinePhones)}` : ''}
                </Pill>
                {unlabelled > 0 && <Badge kind="warn">{t.machines.unlabelled(unlabelled)}</Badge>}
                <span className="grow" />
                <button className="quiet tiny" aria-expanded={expanded}
                        onClick={() => setOpen((o) => ({ ...o, [h.hub_id]: !expanded }))}>
                  {expanded ? <ChevronUp className="ico" size={14} aria-hidden /> : <ChevronDown className="ico" size={14} aria-hidden />}
                  {expanded ? t.machines.hide : `${t.machines.showPhones} (${phones.length})`}
                </button>
                <button
                  className="quiet-destroy tiny"
                  disabled={h.online || !mayManage}
                  title={!mayManage ? t.common.adminOnly : h.online ? t.machines.onlineCannotDelete : t.machines.deleteMachine}
                  onClick={() => {
                    setError(null)
                    setTarget({ kind: 'hub', hub: h, phones: phones.length })
                  }}
                >
                  {t.machines.deleteMachine}
                </button>
              </div>

              {/* The phones that fell off, before the list: with 28 on one machine the one that dropped
                  should not take a scroll to find (owner, 2026-10-10). */}
              {h.online && phones.some((p) => p.status === 'offline') && (
                <div className="usb-dropped">
                  <b className="tiny">{t.machines.usb.dropped}</b>
                  {phones.filter((p) => p.status === 'offline').map((p) => (
                    <div key={p.device_id} className="usb-dropped-row">
                      <span className="small">{phoneName(p)}</span>
                      <UsbState device={p} />
                    </div>
                  ))}
                </div>
              )}

              {expanded && (
                <div className="sublist">
                  {phones.length === 0 && <Empty>{t.machines.noPhones}</Empty>}
                  {byUsbHub(phones).map((g) => [
                    <div key={`g:${g.hub}`} className="usb-group tiny dim">
                      {g.hub ? t.machines.usb.group(g.hub, g.phones.length) : t.machines.usb.unknownPort}
                    </div>,
                    ...g.phones.map((d) => (
                    <div className="subrow" key={d.device_id}>
                      <PhoneIcon
                        status={d.status}
                        size={18}
                        title={
                          d.status === 'offline'
                            ? t.devices.statusOffline
                            : d.status === 'busy'
                              ? t.devices.statusBusy
                              : t.devices.statusOnline
                        }
                      />
                      <span style={{ minWidth: 0 }}>
                        <NameLabel device={d} onSaved={devices.refresh} />
                        <div className="dim tiny mono">
                          {d.name && d.model ? `${d.model} · ` : ''}
                          {d.serial}
                          {d.os_version ? ` · Android ${d.os_version}` : ''}
                        </div>
                        {d.status !== 'offline' && (
                          <div className="usb-line">
                            <UsbSeat device={d} />
                            <BatteryChip device={d} />
                          </div>
                        )}
                        <UsbState device={d} />
                      </span>
                      <AccountLabel device={d} onSaved={devices.refresh} />
                      <SimNumberLabel device={d} onSaved={devices.refresh} />
                      {/* The marketing direction (plan Stage 1): a colour, or "no direction" with the admin's
                          one-time binding beside it. */}
                      <DirectionChip direction={d.direction_id ? dirs.byId.get(d.direction_id) : null} />
                      {!d.direction_id && (
                        <button className="tiny quiet" disabled={!mayManage}
                                title={mayManage ? undefined : t.common.adminOnly}
                                onClick={() => setBinding(d)}>{t.marketing.bindButton}</button>
                      )}
                      <span className="grow" />
                      <DropCount device={d} />
                      {d.adb_state && d.adb_state !== 'device' && <Badge kind="bad">{t.devices.adbTrouble(d.adb_state)}</Badge>}
                      <button
                        className="quiet-destroy tiny"
                        disabled={d.status !== 'offline' || !mayManage}
                        title={!mayManage ? t.common.adminOnly : d.status !== 'offline' ? t.machines.liveCannotDelete : t.machines.deletePhone}
                        onClick={() => {
                          setError(null)
                          setTarget({ kind: 'device', device: d })
                        }}
                      >
                        {t.machines.deletePhone}
                      </button>
                    </div>
                    ))])}
                </div>
              )}
            </div>
          )
        })}
      </Card>

      {binding && (
        <BindDirection device={binding} directions={dirs.list}
                       onCancel={() => setBinding(null)}
                       onDone={() => { setBinding(null); devices.refresh(); dirs.refresh() }} />
      )}

      {target && (
        <ConfirmDelete
          name={target.kind === 'hub' ? target.hub.hub_id : target.device.serial}
          title={
            target.kind === 'hub'
              ? t.confirm.titleMachine(target.hub.hub_id)
              : t.confirm.titlePhone(phoneName(target.device))
          }
          warnings={
            target.kind === 'hub'
              ? [t.confirm.ourDbOnly, t.confirm.cascade(target.phones)]
              : [t.confirm.ourDbOnly, t.confirm.phoneNote]
          }
          busy={busy}
          error={error}
          onCancel={() => setTarget(null)}
          onConfirm={() => void doDelete()}
        />
      )}
    </>
  )
}

/**
 * One field we own on a phone, edited in place: click to edit, Enter or blur to save, Escape to give up.
 * An empty value clears the field. The failure is handed back for the caller to phrase — the account
 * label has a refusal of its own (`account_in_use`) that must name the other phone.
 */
function useOwnedField(
  device: Device,
  field: 'name' | 'tiktok_account' | 'phone_number',
  onSaved: (next: string) => void,
  describeError: (e: unknown, next: string) => string,
) {
  const { api } = useApp()
  const current = device[field] ?? ''
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(current)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setEditing(false)
    const next = value.trim()
    if (next === current) return
    setBusy(true)
    setError(null)
    try {
      await api.updateDevice(device.device_id, { [field]: next || null })
      onSaved(next)
    } catch (e) {
      setError(describeError(e, next))
      setValue(current)
    } finally {
      setBusy(false)
    }
  }

  const start = () => {
    setValue(current)
    setEditing(true)
  }
  const cancel = () => {
    setValue(current)
    setEditing(false)
  }
  return { current, editing, value, setValue, error, busy, save, start, cancel }
}

function hintOf(e: unknown): string {
  const detail = e instanceof ApiError ? (e.detail as { detail?: { hint?: string } })?.detail : null
  return detail?.hint ?? (e instanceof Error ? e.message : String(e))
}

type Editor = ReturnType<typeof useOwnedField>

function OwnedFieldInput({ f, placeholder, label, mono }: { f: Editor; placeholder: string; label: string; mono?: boolean }) {
  return (
    <input
      className={mono ? 'mono' : undefined}
      autoFocus
      value={f.value}
      placeholder={placeholder}
      aria-label={label}
      style={{ width: 200 }}
      onChange={(e) => f.setValue(e.target.value)}
      onBlur={() => void f.save()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') void f.save()
        if (e.key === 'Escape') f.cancel()
      }}
    />
  )
}

/** The phone's name: bold where the model used to be; the model moves to the small line below. */
export function NameLabel({ device, onSaved }: { device: Device; onSaved: () => void }) {
  const { auth } = useApp()
  const t = useT()
  const f = useOwnedField(
    device,
    'name',
    (next) => {
      toast(next ? t.phoneName.set(next, device.model ?? device.serial) : t.phoneName.cleared(device.model ?? device.serial))
      onSaved()
    },
    hintOf,
  )
  return (
    <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
      {f.editing ? (
        <OwnedFieldInput f={f} placeholder={device.model ?? device.serial} label={t.phoneName.label} />
      ) : (
        <button className="chip" title={auth.isAdmin ? t.phoneName.editTitle : t.common.adminOnly}
                disabled={f.busy || !auth.isAdmin} onClick={f.start} style={{ fontWeight: 600 }}>
          {phoneName(device)}
        </button>
      )}
      {f.error && (
        <span className="tiny" style={{ color: 'var(--bad)' }}>
          {f.error}
        </span>
      )}
    </span>
  )
}

/**
 * The TikTok account on one phone.
 *
 * A refusal is shown **beside the field**, in the service's words (`account_in_use` names the phone that
 * already has the label): a toast would vanish before the person understood which phone it meant.
 */
export function AccountLabel({ device, onSaved }: { device: Device; onSaved: () => void }) {
  const { auth } = useApp()
  const t = useT()
  const f = useOwnedField(
    device,
    'tiktok_account',
    (next) => {
      toast(next ? t.account.set(next, phoneName(device)) : t.account.cleared(phoneName(device)))
      onSaved()
    },
    (e, next) => {
      const detail = e instanceof ApiError ? (e.detail as { detail?: { error?: string; device_id?: string } })?.detail : null
      return detail?.error === 'account_in_use' ? t.account.inUse(next, detail.device_id ?? '?') : hintOf(e)
    },
  )

  return (
    <span className="account" style={{ display: 'grid', gap: 2, minWidth: 0 }}>
      {f.editing ? (
        <OwnedFieldInput f={f} placeholder={t.account.placeholder} label={t.account.label} mono />
      ) : (
        <button className={`chip account-chip${f.current ? ' on' : ''}`} title={auth.isAdmin ? t.account.editTitle : t.common.adminOnly}
                disabled={f.busy || !auth.isAdmin} onClick={f.start}>
          {f.current ? `@${f.current.replace(/^@/, '')}` : t.account.none}
        </button>
      )}
      {f.error && (
        <span className="tiny" style={{ color: 'var(--bad)' }}>
          {f.error}
        </span>
      )}
    </span>
  )
}

/** The phones grouped by the hub they hang on (the computer's USB port), in place order — what a person
 *  standing at the shelf needs. Phones whose port is not known yet come last. */
function byUsbHub(phones: Device[]): { hub: string | null; phones: Device[] }[] {
  const groups = new Map<string | null, Device[]>()
  for (const d of phones) {
    const hub = usbPlace(d.usb_path)?.hub ?? null
    groups.set(hub, [...(groups.get(hub) ?? []), d])
  }
  const order = (d: Device) => usbPlace(d.usb_path)?.place ?? 99
  return [...groups.entries()]
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : Number(a) - Number(b)))
    .map(([hub, list]) => ({ hub, phones: list.sort((x, y) => order(x) - order(y) || (x.usb_path ?? '').localeCompare(y.usb_path ?? '')) }))
}

/** The SIM's number on the phone (owner, 2026-10-10: "щоб було видно одразу"). Same editing as the account. */
export function SimNumberLabel({ device, onSaved }: { device: Device; onSaved: () => void }) {
  const { auth } = useApp()
  const t = useT()
  const f = useOwnedField(
    device,
    'phone_number',
    (next) => {
      toast(next ? t.simNumber.set(next, phoneName(device)) : t.simNumber.cleared(phoneName(device)))
      onSaved()
    },
    (e, next) => {
      const detail = e instanceof ApiError ? (e.detail as { detail?: { error?: string; device_id?: string } })?.detail : null
      return detail?.error === 'phone_number_in_use' ? t.simNumber.inUse(next, detail.device_id ?? '?') : hintOf(e)
    },
  )
  return (
    <span className="account" style={{ display: 'grid', gap: 2, minWidth: 0 }}>
      {f.editing ? (
        <OwnedFieldInput f={f} placeholder={t.simNumber.placeholder} label={t.simNumber.label} mono />
      ) : (
        <button className={`chip account-chip${f.current ? ' on' : ''}`} title={auth.isAdmin ? t.simNumber.editTitle : t.common.adminOnly}
                disabled={f.busy || !auth.isAdmin} onClick={f.start}>
          {f.current ? formatPhone(f.current) : t.simNumber.none}
        </button>
      )}
      {f.error && <span className="tiny" style={{ color: 'var(--bad)' }}>{f.error}</span>}
    </span>
  )
}

/** "+380671234567" → "+380 67 123 45 67" for reading; other lengths are shown as stored. */
function formatPhone(n: string): string {
  const m = /^\+380(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(n)
  return m ? `+380 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : n
}
